<svelte:options runes={true} />

<script lang="ts">
  import IconCheck from "~icons/dusk/check";
  import IconMinus from "~icons/dusk/minus";
  import { Checkbox } from "bits-ui";

  interface Props {
    id?: string;
    checked?: boolean;
    indeterminate?: boolean;
    ref?: HTMLButtonElement | null;
    value?: string;
    disabled?: boolean;
    onChange?: (checked: boolean) => void;
    class?: string;
    "aria-label"?: string;
    "aria-labelledby"?: string;
  }

  let {
    id,
    checked = $bindable(false),
    indeterminate = $bindable(false),
    ref = $bindable<HTMLButtonElement | null>(null),
    value,
    disabled,
    onChange,
    class: className = "",
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
  }: Props = $props();
</script>

<Checkbox.Root
  {id}
  bind:checked
  bind:indeterminate
  bind:ref
  {value}
  {disabled}
  onCheckedChange={onChange}
  aria-label={ariaLabel}
  aria-labelledby={ariaLabelledBy}
  class="da-checkbox {className}"
>
  {#snippet children({ checked: isChecked, indeterminate: isIndeterminate })}
    <span class="da-checkbox__indicator" aria-hidden="true">
      {#if isIndeterminate}
        <IconMinus aria-hidden="true" width="10" height="10" />
      {:else if isChecked}
        <IconCheck aria-hidden="true" width="12" height="12" />
      {/if}
    </span>
  {/snippet}
</Checkbox.Root>

<style>
  @layer aurora {
    /* bits-ui renders the button element — use :global() so Svelte doesn't strip these rules */
    :global(.da-checkbox) {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      background: var(--da-color-white);
      border: 1px solid var(--da-color-charcoal-200);
      border-radius: var(--da-radius-sm);
      flex-shrink: 0;
      cursor: pointer;
      outline: none;
      transition:
        background var(--da-duration-fast),
        border-color var(--da-duration-fast);
    }

    :global(.da-checkbox[data-state="checked"]) {
      background: var(--da-color-light-sand-500);
      border-color: var(--da-color-charcoal-200);
    }

    :global(.da-checkbox[data-disabled]) {
      opacity: 0.5;
      cursor: not-allowed;
    }

    :global(.da-checkbox__indicator) {
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--da-color-charcoal-500);
      pointer-events: none;
    }
  }
</style>
